import { useEventCallback } from '@ixigo/iui/utils';
import { useCallback, useEffect, useRef, useState } from 'react';

export interface UseAsyncListOptions<T> {
	/**
	 * The function to load the list.
	 * gets debounced and aborted on new query.
	 * @param query - The query to load the list.
	 * @param opts - The options for the load.
	 */
	load?: (
		query: string,
		opts: { signal: AbortSignal },
	) => Promise<{
		items: T[];
	}>;
	/**
	 * The debounce time in milliseconds.
	 * @default 200
	 */
	debounceMs?: number;
	/**
	 * The minimum length of the query to initiate a load.
	 * @default 2
	 */
	minQueryLength?: number;
	/**
	 * Whether to preload the list on mount.
	 * pass any truthy value to preload the list on mount.
	 * if it changes, suggestion will be refetched.
	 */

	// biome-ignore lint/suspicious/noExplicitAny: it's being used as dependency for cache invalidation
	preload?: any;
	/**
	 * The query to preload.
	 */
	preloadQuery?: string;
}

export interface UseAsyncListReturn<T> {
	/**
	 * The items in the list.
	 */
	items: T[];
	/**
	 * Whether the list is loading.
	 */
	loading: boolean;
	/**
	 * The current query.
	 */
	query?: string;
	/**
	 * The function to set the query.
	 */
	setQuery: (q?: string) => void; // initiates debounced load and aborts previous
}

export function useAsyncList<T>(
	options: UseAsyncListOptions<T>,
): UseAsyncListReturn<T> {
	const {
		load: loadProp,
		debounceMs = 200,
		minQueryLength = 1,
		preload,
		preloadQuery = '',
	} = options;

	const [state, setState] = useState<{
		items: T[];
		loading: boolean;
		preloadData?: T[];
		preloadKey?: string;
		query?: string;
	}>({
		items: [],
		loading: false,
	});

	const cancelRef = useRef<{
		controller?: AbortController;
		timer?: ReturnType<typeof setTimeout>;
	}>({});

	const load = useEventCallback(
		({ query, preload }: { query?: string; preload?: boolean }) => {
			if (!loadProp) return;
			const q = query ?? (preload ? preloadQuery : '');
			// Cancel any scheduled run
			const ref = cancelRef.current;
			clearTimeout(ref.timer);
			ref.controller?.abort();
			ref.controller = undefined;

			const canQuery = q.length >= minQueryLength || preload;
			if (!canQuery) {
				// If the query is too short, ensure loading is not shown
				setState((prev) => ({ ...prev, loading: false }));
				return;
			}

			// Indicate loading immediately during debounce to avoid transient empty states
			setState((prev) => ({ ...prev, loading: true }));

			const execute = async () => {
				const controller = new AbortController();
				ref.controller = controller;
				const dataKey = preload ? 'preloadData' : 'items';
				try {
					const res = await loadProp(q, { signal: controller.signal });
					setState((prev) => ({
						...prev,
						[dataKey]: res.items,
						preloadKey: String(preload),
						loading: false,
					}));
				} catch {
					if (controller.signal.aborted) return;
					setState((prev) => ({
						...prev,
						[dataKey]: [],
						preloadKey: String(preload),
						loading: false,
					}));
				}
			};

			if (preload) {
				execute();
				return;
			}

			ref.timer = setTimeout(execute, debounceMs);
		},
	);

	const setQuery = useCallback(
		(q?: string) => {
			setState((prev) => ({ ...prev, query: q }));
			load({ query: q });
		},
		[load],
	);

	// Preload once on mount if requested
	useEffect(() => {
		load({ preload });
	}, [load, preload]);

	// Cleanup on unmount
	useEffect(() => {
		return () => {
			const ref = cancelRef.current;
			clearTimeout(ref.timer);
			ref.controller?.abort();
		};
	}, []);

	const preloadItems =
		String(preload) === state.preloadKey ? (state.preloadData ?? []) : [];

	return {
		items: state.items.length ? state.items : preloadItems,
		loading: state.loading,
		query: state.query,
		setQuery,
	};
}
